/**
 * New Request View
 *
 * Page view for creating a new expenditure request.
 * Uses the RequestForm component.
 */

import { createRequestForm, type RequestFormData, type QueuedFile } from '../components/request-form';
import { createSidebarLayout } from '../components/sidebar-layout';
import { toastSuccess, toastError, toastInfo } from '../components/toast';
import { renderIcon } from '../utils/icons';
import { apiClient } from '../services/api-client';

/**
 * Create the new request view
 */
export function newRequestView(): HTMLElement {
  // Create main content
  const content = document.createElement('div');
  content.className = 'px-4 lg:px-6 py-6 max-w-3xl mx-auto';

  // Form container
  const formContainer = document.createElement('div');
  formContainer.id = 'request-form-container';

  content.appendChild(formContainer);

  // Initialize the form after container is in DOM
  setTimeout(async () => {
    try {
      await createRequestForm({
        container: formContainer,
        onSubmit: handleSubmit,
        onSaveDraft: handleSaveDraft,
      });
    } catch (error) {
      console.error('Failed to initialize request form:', error);
      formContainer.innerHTML = `
        <div class="glass-card p-8 rounded-2xl text-center">
          <div class="flex justify-center mb-4">
            ${renderIcon('alertCircle', { size: 'xl', className: 'text-destructive' }).outerHTML}
          </div>
          <h2 class="text-xl font-bold text-foreground mb-2">Failed to Load Form</h2>
          <p class="text-muted-foreground mb-4">We couldn't load the request form. Please try again.</p>
          <button onclick="location.reload()" class="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors">
            ${renderIcon('refresh', { size: 'sm' }).outerHTML}
            <span>Retry</span>
          </button>
        </div>
      `;
    }
  }, 0);

  // Wrap with sidebar layout
  return createSidebarLayout(content, {
    currentPath: '/requests/new',
    title: 'New Request',
    subtitle: 'Create a new expenditure request',
  });
}

/**
 * Upload a single file to a request
 */
async function uploadFileToRequest(requestId: string, file: File): Promise<{ id: string }> {
  const formData = new FormData();
  formData.append('files', file);

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const response = JSON.parse(xhr.responseText);
          const attachmentId = response.data?.[0]?.id;
          if (attachmentId) {
            resolve({ id: attachmentId });
          } else {
            reject(new Error('Invalid response from server'));
          }
        } catch {
          reject(new Error('Failed to parse server response'));
        }
      } else {
        try {
          const errorResponse = JSON.parse(xhr.responseText);
          reject(new Error(errorResponse.message || 'Upload failed'));
        } catch {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Network error during upload'));
    });

    xhr.open('POST', `/api/requests/${requestId}/attachments`);

    // Get auth token
    const token = localStorage.getItem('auth_token');
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }

    xhr.send(formData);
  });
}

/**
 * Upload all queued files to a request
 */
async function uploadQueuedFiles(requestId: string, files: QueuedFile[]): Promise<{ uploaded: number; failed: number }> {
  let uploaded = 0;
  let failed = 0;

  for (const queuedFile of files) {
    try {
      await uploadFileToRequest(requestId, queuedFile.file);
      uploaded++;
    } catch (error) {
      console.error(`Failed to upload file ${queuedFile.name}:`, error);
      failed++;
    }
  }

  return { uploaded, failed };
}

/**
 * Handle form submission (create and submit request)
 */
async function handleSubmit(data: RequestFormData): Promise<void> {
  try {
    // Create the request (as DRAFT initially)
    const response = await apiClient.post<{ data: { id: string }; message: string }>('/requests', {
      title: data.title,
      description: data.description,
      amount: parseFloat(data.amount),
      currency: data.currency,
      category: data.expenseType,
      frequency: data.frequency,
    });

    const requestId = response.data.data.id;

    // Upload queued files if any
    const queuedFiles = data.queuedFiles || [];
    if (queuedFiles.length > 0) {
      toastInfo(`Uploading ${queuedFiles.length} file${queuedFiles.length > 1 ? 's' : ''}...`, {
        title: 'Uploading Attachments',
        duration: 3000,
      });

      const uploadResult = await uploadQueuedFiles(requestId, queuedFiles);

      if (uploadResult.failed > 0) {
        toastError(`${uploadResult.failed} file${uploadResult.failed > 1 ? 's' : ''} failed to upload. You can add them later by editing the request.`, {
          title: 'Some Uploads Failed',
          duration: 8000,
        });
      } else if (uploadResult.uploaded > 0) {
        toastSuccess(`${uploadResult.uploaded} file${uploadResult.uploaded > 1 ? 's' : ''} uploaded successfully`, {
          title: 'Files Uploaded',
          duration: 3000,
        });
      }
    }

    // Submit the request for approval (changes status from DRAFT to PENDING)
    await apiClient.post(`/requests/${requestId}/submit`);

    // Show success toast
    toastSuccess('Request submitted successfully', {
      title: 'Request Submitted',
      duration: 5000,
      action: {
        label: 'View Request',
        onClick: () => {
          window.location.href = `/requests/${requestId}`;
        },
      },
    });

    // Navigate to dashboard
    window.location.href = '/dashboard';
  } catch (error) {
    console.error('Failed to submit request:', error);

    const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';

    // Show error toast with retry option
    toastError(`Failed to submit request: ${errorMessage}`, {
      title: 'Submission Failed',
      action: {
        label: 'Retry',
        onClick: () => handleSubmit(data),
        variant: 'primary',
      },
    });

    throw error; // Re-throw so the form knows submission failed
  }
}

/**
 * Handle save as draft
 */
async function handleSaveDraft(data: RequestFormData): Promise<void> {
  try {
    // Create the request as draft
    const response = await apiClient.post<{ data: { id: string }; message: string }>('/requests', {
      title: data.title,
      description: data.description || '',
      amount: data.amount ? parseFloat(data.amount) : 0,
      currency: data.currency || 'CHF',
      category: data.expenseType || 'OTHER',
      frequency: data.frequency || 'ONE_TIME',
    });

    const requestId = response.data.data.id;

    // Upload queued files if any
    const queuedFiles = data.queuedFiles || [];
    if (queuedFiles.length > 0) {
      toastInfo(`Uploading ${queuedFiles.length} file${queuedFiles.length > 1 ? 's' : ''}...`, {
        title: 'Uploading Attachments',
        duration: 3000,
      });

      const uploadResult = await uploadQueuedFiles(requestId, queuedFiles);

      if (uploadResult.failed > 0) {
        toastError(`${uploadResult.failed} file${uploadResult.failed > 1 ? 's' : ''} failed to upload. You can add them later by editing the draft.`, {
          title: 'Some Uploads Failed',
          duration: 8000,
        });
      } else if (uploadResult.uploaded > 0) {
        toastSuccess(`${uploadResult.uploaded} file${uploadResult.uploaded > 1 ? 's' : ''} uploaded successfully`, {
          title: 'Files Uploaded',
          duration: 3000,
        });
      }
    }

    // Show success toast
    toastSuccess('Draft saved successfully', {
      title: 'Draft Saved',
      duration: 5000,
      action: {
        label: 'Edit Draft',
        onClick: () => {
          window.location.href = `/requests/${requestId}/edit`;
        },
      },
    });

    // Navigate to my requests
    window.location.href = '/dashboard';
  } catch (error) {
    console.error('Failed to save draft:', error);

    const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';

    // Show error toast with retry option
    toastError(`Failed to save draft: ${errorMessage}`, {
      title: 'Save Failed',
      action: {
        label: 'Retry',
        onClick: () => handleSaveDraft(data),
        variant: 'primary',
      },
    });

    throw error; // Re-throw so the form knows save failed
  }
}

/**
 * Factory function for the router
 */
export function createNewRequestView(): HTMLElement {
  return newRequestView();
}

export default newRequestView;
